import React, { FC } from 'react';
import { Box, MantineColor, Text, Tooltip } from '@mantine/core';
import { Cell, Pie, PieChart, ResponsiveContainer, Sector } from 'recharts';
import { NumberUtils } from '@shared/utils/number';
import { useTheme } from '@emotion/react';
import { useDisclosure } from '@mantine/hooks';
import { BoxProps } from '@mantine/core/lib/Box/Box';
import formatPercent = NumberUtils.formatPercent;

const PADDING_ANGLE = 8;

const ActiveShape = ({ outerRadius, innerRadius, color, endAngle, value, ...props }: any) => {
  const theme = useTheme();
  return (
    <Tooltip label={formatPercent(value)} withinPortal>
      <g>
        <Sector {...props} innerRadius={innerRadius} outerRadius={outerRadius} endAngle={endAngle} />
        <Sector
          {...props}
          innerRadius={innerRadius - 18}
          outerRadius={innerRadius - 14}
          fill={theme.colors.gray[4]}
          endAngle={endAngle - PADDING_ANGLE}
        />
      </g>
    </Tooltip>
  );
};

interface AlertsPieChartData {
  value: number;
  bg: MantineColor;
  active: boolean;
}

interface AlertsPieChartProps {
  data: Array<AlertsPieChartData>;
  value: number;
}

const AlertsPieChart: FC<AlertsPieChartProps & BoxProps> = ({ data, value, ...rest }) => {
  const [mounted, handlers] = useDisclosure(false);
  const theme = useTheme();
  const activeIndexes = data.reduce<Array<number>>((acc, curr, index) => (curr.active ? [...acc, index] : acc), []);

  return (
    <Box h="100%" style={{ overflow: 'hidden' }} {...rest}>
      <Box pos="relative" h="200%">
        <ResponsiveContainer>
          <PieChart>
            <Pie
              data={data}
              cy="50%"
              cx="50%"
              innerRadius="75%"
              outerRadius="100%"
              startAngle={180}
              endAngle={0}
              paddingAngle={PADDING_ANGLE}
              labelLine={false}
              dataKey="value"
              onAnimationEnd={handlers.open}
              animationDuration={0}
              activeShape={<ActiveShape />}
              activeIndex={activeIndexes}
            >
              {data.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={theme.fn.themeColor(entry.bg)} strokeWidth={0} />
              ))}
            </Pie>
          </PieChart>
        </ResponsiveContainer>

        {mounted && (
          <Box pos="absolute" bottom="50%" left="50%" ta="center" style={{ transform: 'translateX(-50%)' }}>
            <Text size={14} color="tertiary.8" weight="bold" lh={1.43}>
              {formatPercent(value)}
            </Text>
            <Text tt="uppercase" color="rgba(46, 46, 46, 0.4)" size={12} weight={500} lh={1.33} pt={5}>
              Taux d'alertes
            </Text>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default AlertsPieChart;
